/* ============================================================
   NalogReceiptSection — 15c2a311 (12754a4b part 2): the per-order
   tax-receipt row on the provider order detail. Appears only when a
   receipt exists (GET 404 → hidden): status chip, amount, short uuid,
   failure reason, cancel with the default comment «Чек сформирован
   ошибочно» (sent by the backend) and file download (POST fetch+cache,
   GET streamed pdf/png).
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { NalogReceipt } from '../types/nalog';
import { buildNalogReceiptView } from './nalogView';

export function NalogReceiptSection({ orderId }: { orderId: string }) {
  const [receipt, setReceipt] = useState<NalogReceipt | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    try {
      setReceipt(await api.getNalogReceipt(id));
      setHidden(false);
    } catch {
      // No receipt for this order (or not visible) — hide the block.
      setHidden(true);
      setReceipt(null);
    }
  }, []);

  useEffect(() => {
    setConfirmCancel(false);
    setError(null);
    void load(orderId);
  }, [orderId, load]);

  if (hidden && receipt === null) return null;
  if (!receipt) return null;

  const view = buildNalogReceiptView(receipt);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      setReceipt(await api.cancelNalogReceipt(orderId));
      setConfirmCancel(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось аннулировать чек');
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await api.downloadNalogReceiptFile(orderId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nalog-receipt-${orderId.slice(0, 8)}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось скачать чек');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Чек «Мой налог»
      </p>

      <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-caption)' }}>
        <span className={view.badgeClass}>{view.statusLabel}</span>
        <span style={{ fontWeight: 'var(--fw-medium)' }}>{view.amountLabel}</span>
        {view.uuidTail && (
          <span style={{ color: 'var(--color-text-secondary)' }}>№ …{view.uuidTail}</span>
        )}
        {view.canDownload && (
          <button type="button" className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={downloading} onClick={() => void download()}>
            {downloading ? '⏳ …' : '⬇ Скачать чек'}
          </button>
        )}
        {view.canCancel && !confirmCancel && (
          <button type="button" className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }} disabled={busy} onClick={() => setConfirmCancel(true)}>
            Аннулировать
          </button>
        )}
      </div>

      {view.problemText && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{view.problemText}</p>
      )}
      {view.cancelComment && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Аннулирован: «{view.cancelComment}»
        </p>
      )}

      {confirmCancel && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 220 }}>
            Отменить чек в «Мой налог» с комментарием «Чек сформирован ошибочно»?
          </span>
          <button type="button" className="btn btn-secondary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => void cancel()}>
            {busy ? 'Аннулирование…' : 'Аннулировать'}
          </button>
          <button type="button" className="btn btn-tertiary" style={{ fontSize: 'var(--fs-caption)' }} disabled={busy} onClick={() => setConfirmCancel(false)}>
            Отмена
          </button>
        </div>
      )}

      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
    </div>
  );
}
